import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, ServerIcon, CubeTransparentIcon } from '@heroicons/react/24/outline';

const ValidatorGuide: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary-900 to-primary-800 text-white pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl pt-8">
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-5xl font-bold mb-6"
            >
              Validator Guide
            </motion.h2>
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-xl mb-8"
            >
              Join the network of validators securing Eigensurance's decentralized insurance platform through EigenLayer's AVS framework.
            </motion.p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          <motion.div 
            whileHover={{ scale: 1.05 }}
            className="p-6 rounded-xl border border-secondary-200"
          >
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
              <ShieldCheckIcon className="w-6 h-6 text-primary-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Restaking Security</h3>
            <ul className="list-disc list-inside text-secondary-600 mt-2 space-y-1">
              <li>Invalid Computation Results</li>
              <li>Malicious Behavior</li>
              <li>Offline Penalties</li>
              <li>Consensus Violations</li>
            </ul>
          </motion.div>

          <motion.div 
            whileHover={{ scale: 1.05 }}
            className="p-6 rounded-xl border border-secondary-200"
          >
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
              <ServerIcon className="w-6 h-6 text-primary-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Technical Requirements</h3>
            <ul className="list-disc list-inside text-secondary-600 mt-2 space-y-1">
              <li>High-performance GPUs for AI inference</li>
              <li>Reliable network connectivity</li>
              <li>Secure key management</li>
              <li>Monitoring capabilities</li>
            </ul>
          </motion.div>

          <motion.div 
            whileHover={{ scale: 1.05 }}
            className="p-6 rounded-xl border border-secondary-200"
          >
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
              <CubeTransparentIcon className="w-6 h-6 text-primary-600" />
            </div>
            <h3 className="text-xl font-semibold mb-2">Rewards Structure</h3>
            <ul className="list-disc list-inside text-secondary-600 mt-2 space-y-1">
              <li>Policy issuance fees</li>
              <li>Claims processing rewards</li>
              <li>Data validation incentives</li>
              <li>Staking yields</li>
            </ul>
          </motion.div>
        </div>

        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold mb-6">Validation Process</h2>
          <div className="space-y-6">
            <div>
              <h3 className="text-xl font-semibold mb-2">1. Node Setup</h3>
              <p className="text-secondary-600">
                Set up your validator node with the required hardware and software specifications. 
                Install the Eigensurance AVS software and configure your environment.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">2. Stake Delegation</h3>
              <p className="text-secondary-600">
                Delegate your ETH stake through EigenLayer's contracts. Choose your stake amount 
                and register as an operator for the Eigensurance AVS.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">3. Validation Duties</h3>
              <p className="text-secondary-600">
                Participate in consensus for insurance operations, validate claims data, 
                and maintain high availability for network operations.
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-2">4. Monitoring & Maintenance</h3>
              <p className="text-secondary-600">
                Keep your node updated, monitor performance metrics, and ensure compliance 
                with network requirements to avoid penalties.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ValidatorGuide; 